/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {Log} from "../utils/Log";
import {SecurityAgent} from "../e2ee/SecurityAgent.js";
import {HubInfo} from "./HubInfo.js";
import {Actor} from "./Actor.js";
import {Utils} from "../utils/Utils";
import {ExecutionMessage, NoEncoding} from "./ExecutionMessage.js";


/**
 * WebSocket transport for the Actor protocol.
 *
 * The Actor base class owns protocol logic; this class focuses on connection lifecycle and
 * strict send failure propagation so RPC helpers can surface local-send errors reliably.
 */
export class WebSocketActor extends Actor {

    /**
     * @type {WebSocket|null}
     */
    ws = null;

    ////////////////////////////////////////
    //  Static and designated constructors
    ////////////////////////////////////////

    /**
     * @param {string} hubName
     * @param {string} hubCode
     * @param {CryptoKey|string} aesKey
     * @param {string} actorName
     * @param {string} actorActivation
     * @param {string|undefined} host
     * @return {Promise<WebSocketActor>}
     */
    static async createHubAndActor(hubName, hubCode, aesKey, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID(), host = HubInfo.defaultHost()) {
        const hubInfos = await HubInfo.createHub(hubName, hubCode, aesKey, host);
        return WebSocketActor.connected(hubInfos, actorName, actorActivation);
    }

    /**
     * @param {string} hubName
     * @param {string} hubCode
     * @param {string} actorName
     * @param {string} actorActivation
     * @param {string|undefined} host
     * @return {Promise<WebSocketActor>}
     */
    static async createActor(hubName, hubCode, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID(), host = HubInfo.defaultHost()) {
        const hubInfos = await HubInfo.get(hubName, hubCode, host);
        return WebSocketActor.connected(hubInfos, actorName, actorActivation);
    }

    /**
     * @param {HubInfo} hubInfos
     * @param {string} actorName
     * @param {string} actorActivation
     * @return {Promise<WebSocketActor>}
     * @constructor
     */
    static async connected(hubInfos, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID()) {
        if (!hubInfos) {
            throw new Error("hub is invalid")
        }
        const actor = new WebSocketActor(hubInfos, actorName, actorActivation);
        const hubUrl = `${actor.wsHost}/api/v1/actors/connect/to/${hubInfos.name}`;
        await actor.connect(hubUrl);
        return Promise.resolve(actor);
    }

    /**
     * @param {HubInfo} hubInfos
     * @param {string} actorName
     * @param {string} actorActivation
     * @constructor
     */
    constructor(hubInfos, actorName = "", actorActivation = "") {
        super(hubInfos, actorName, actorActivation);
        this.wsHost = SecurityAgent.wsHostFrom(hubInfos.host);
        this.calls = {};
        this.name = actorName;
        this.activation = actorActivation;
    }

    ////////////////////////////////////////
    //  Connection / Disconnection
    ////////////////////////////////////////


    /**
     * @param hubUrl
     * @return {Promise<unknown>}
     */
    async connectTo(hubUrl) {
        return new Promise((resolve, _) => {
            this.ws = new WebSocket(hubUrl);
            this.ws.onmessage = async (event) => {
                try{
                    await this.handleIncomingMessage(event.data);
                }catch (e){
                    Log.error("[WebSocketActor.connect.ws.onmessage] exception: ", e)
                }
            };

            this.ws.onopen = () => {
                Log.info('[WebSocketActor.connect.ws.onopen] WebSocket connection established ', this.hubInfo.name, this.name ? `as ${this.name}` : '');
                // Reset reconnection attempts on successful connection to avoid pathological backoff after transient network events.
                this._reconnectAttempts = 0;
                this._reconnectDelay = 1000;
                this.connected = true;
                resolve(true);
            };

            this.ws.onclose = () => {
                Log.info('[WebSocketActor.connect.ws.onclose] WebSocket connection closed ', this.hubInfo.name);
                if (!this.explicitlyDisconnected) {
                    this.connectionAttempt(hubUrl, true);
                } else {
                    this.ws = null;
                }
            };

            this.ws.onerror = (errorEvent) => {
                const errorMessage = errorEvent.message || "Unknown WebSocket error";
                const errorType = errorEvent.type || "error";
                Log.error(`[WebSocketActor.connect.ws.onerror] WebSocket error: Type - ${errorType}, Message - ${errorMessage}`, errorEvent);
                if (!this.connected) {
                    resolve(false);
                }
            };
        });
    }


    async disconnect() {
        await super.disconnect();
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        return Promise.resolve();
    }


    /**
     * @param {ExecutionMessage} message
     * @param {string} [encoding=NoEncoding]
     * @param {number} [timeOut=5000]
     * @returns {Promise<void>}
     */
    sendExecutionMessage(message, encoding=NoEncoding, timeOut=5000) {
        return message.transform((textualMessage)=>{
            return this._encryptAndSendToWs(textualMessage);
        });
    }

    /**
     * Strictly propagate send failures so RPC helpers can surface local-send errors consistently.
     * @param textualMessage
     * @return {Promise<void>}
     * @private
     */
    _encryptAndSendToWs(textualMessage) {
        return SecurityAgent.encrypt(this.hubInfo.aesKey, textualMessage)
            .then((encryptedMessage) => {
                try {
                    if (!this.ws) {
                        throw new Error("WebSocket is null");
                    }
                    // Fail fast with a meaningful error rather than relying on ws.send throwing.
                    if (this.ws.readyState !== WebSocket.OPEN) {
                        throw new Error(`WebSocket is not open (readyState=${this.ws.readyState})`);
                    }
                    this.ws.send(encryptedMessage);
                } catch (e) {
                    Log.error('[Actor.sendMessage] Error sending encrypted message:', e.message);
                    return Promise.reject(e);
                }
                return Promise.resolve();
            })
            .catch((e) => {
                Log.error('[Actor.sendMessage] Error ciphering message:', e.message);
                return Promise.reject(e);
            });
    }
}